import type {
  DataGridColumn,
  DataGridColumnOrderState,
  DataGridColumnPinningState,
  DataGridColumnSizingState,
  DataGridColumnVisibilityState,
  DataGridGroupColumn,
} from '@ibsys/kit';
import type { SearchConversationFilters, SearchDialogsPage, SearchDownloadHeaderColumn } from '@/entities/search';
import { hasContentSearchQuery, readContentSearchMode } from '@/features/smart-search';
import { orderColumnIdsAsInTable } from './columnsOrder';
import { ATTRIBUTE_COLUMN_PREFIX, EXPERT_REPORT_PREFIX } from './constants';
import { getSnippetColumnId, pickContentSearchColumns } from './contentSearchColumnHelpers';

/** Ширина колонки, которую грид берёт, пока её не задали ни колонка, ни пользователь */
const DEFAULT_COLUMN_WIDTH = 160;

/** Часть идентификатора колонки блока заключения, отделяющая блок от стандартной колонки */
const BLOCK_COLUMN_SEGMENT = 'block_';

/** Ячейка шапки, к которой пришли по дереву колонок */
type HeaderGroup = {
  id: string;
  title: string;
};

/** Колонка данных с ячейками шапки над ней */
type HeaderLeaf = {
  columnId: string;
  key: string;
  title: string;
  width: number;
  /** Группы над колонкой сверху вниз; пустой массив — колонка стоит без группы */
  groups: HeaderGroup[];
};

/** Заголовок ячейки шапки; заголовок-элемент разметки текстом не назвать — он остаётся пустым */
const getHeaderTitle = (header: unknown): string => (typeof header === 'string' ? header : '');

const isGroupColumn = <TData>(column: DataGridColumn<TData>): column is DataGridGroupColumn<TData> =>
  'columns' in column;

/**
 * Код колонки данных в контракте выгрузки: им шапка называет колонку, значения которой вернул
 * поиск. Колонка поиска по содержимому зовётся собственным идентификатором: её значения приносит
 * сам режим поиска, а не запрошенный состав колонок.
 */
export const toDownloadColumnKey = (columnId: string): string => {
  if (columnId.startsWith(ATTRIBUTE_COLUMN_PREFIX)) {
    return `attr:${columnId.slice(ATTRIBUTE_COLUMN_PREFIX.length)}`;
  }

  if (columnId.startsWith(EXPERT_REPORT_PREFIX)) {
    const rest = columnId.slice(EXPERT_REPORT_PREFIX.length);
    const separator = rest.indexOf('_');
    if (separator === -1) return columnId;

    const reportId = rest.slice(0, separator);
    const column = rest.slice(separator + 1);
    if (column.startsWith(BLOCK_COLUMN_SEGMENT)) {
      return `expertReport:${reportId}:block:${column.slice(BLOCK_COLUMN_SEGMENT.length)}`;
    }
    return `expertReport:${reportId}:${column}`;
  }

  return columnId;
};

/**
 * Коды колонок, значения которых запрошены у поиска составом колонок файла. Лист с кодом, которого
 * выдача не отдаёт, файл отвергает.
 */
export const collectDownloadColumnKeys = (dialogsPage: SearchDialogsPage): Set<string> => {
  const keys = new Set<string>(dialogsPage.mainColumns ?? []);

  for (const path of dialogsPage.attributes ?? []) keys.add(`attr:${path}`);

  for (const report of dialogsPage.expertReport ?? []) {
    for (const column of report.columns ?? []) keys.add(`expertReport:${report.id}:${column}`);
    for (const blockId of report.blockIds ?? []) keys.add(`expertReport:${report.id}:block:${blockId}`);
  }

  return keys;
};

/**
 * Коды колонок поиска по содержимому, допустимые в шапке. Состав колонок файла их не запрашивает —
 * значения приносит сама выдача, пока отбор включает режим поиска, — поэтому допустимость читается
 * из того же отбора, что уходит за строками файла, и теми же правилами, которыми его собирали:
 * фрагмент — пока режим действует, релевантность — только с непустым запросом.
 */
const collectContentSearchColumnKeys = (filters: SearchConversationFilters | undefined): string[] => {
  const mode = readContentSearchMode(filters);
  if (mode == null) return [];

  return pickContentSearchColumns(
    { relevance: 'relevance', snippet: getSnippetColumnId(mode) },
    hasContentSearchQuery(filters),
  );
};

/** Разворачивает дерево колонок в колонки данных, помня группы, под которыми каждая стоит */
const collectLeaves = <TData>(
  columns: DataGridColumn<TData>[],
  groups: HeaderGroup[],
  columnSizing: DataGridColumnSizingState,
  leaves: HeaderLeaf[],
): void => {
  for (const column of columns) {
    if (isGroupColumn(column)) {
      // Группа панели управления колонками ячейки в шапке не занимает — её колонки стоят плоско
      const nested = column.panelOnly ? groups : [...groups, { id: column.id, title: getHeaderTitle(column.header) }];
      collectLeaves(column.columns, nested, columnSizing, leaves);
      continue;
    }

    leaves.push({
      columnId: column.id,
      key: toDownloadColumnKey(column.id),
      title: getHeaderTitle(column.header),
      width: columnSizing[column.id] ?? column.size ?? DEFAULT_COLUMN_WIDTH,
      groups,
    });
  }
};

/**
 * Собирает ячейки шапки над колонками: подряд идущие колонки одной группы объединяются, а
 * разорванная перестановкой группа даёт по ячейке на каждый непрерывный участок — так же, как
 * её рисует таблица.
 */
const buildHeaderNodes = (leaves: HeaderLeaf[], level: number): SearchDownloadHeaderColumn[] => {
  const nodes: SearchDownloadHeaderColumn[] = [];
  let index = 0;

  while (index < leaves.length) {
    const group = leaves[index].groups[level];
    if (!group) {
      const { title, key, width } = leaves[index];
      nodes.push({ title, key, width });
      index += 1;
      continue;
    }

    let end = index + 1;
    while (end < leaves.length && leaves[end].groups[level]?.id === group.id) end += 1;

    nodes.push({ title: group.title, columns: buildHeaderNodes(leaves.slice(index, end), level + 1) });
    index = end;
  }

  return nodes;
};

/** Что нужно знать о таблице, чтобы повторить её шапку в файле */
type BuildHeaderParams<TData> = {
  /** Определения колонок таблицы деревом */
  columns: DataGridColumn<TData>[];
  columnOrder: DataGridColumnOrderState;
  columnPinning: DataGridColumnPinningState;
  columnVisibility: DataGridColumnVisibilityState;
  columnSizing: DataGridColumnSizingState;
  /** Состав колонок файла: колонку вне него шапка назвать не может */
  dialogsPage: SearchDialogsPage;
  /** Отбор, с которым уходит запрос файла: им решается, какие колонки поиска шапке доступны */
  filters?: SearchConversationFilters;
};

/**
 * Шапка таблицы деревом для выгрузки: видимые колонки в порядке таблицы, с их заголовками,
 * шириной и объединениями над группами.
 */
export const buildDownloadHeader = <TData>({
  columns,
  columnOrder,
  columnPinning,
  columnVisibility,
  columnSizing,
  dialogsPage,
  filters,
}: BuildHeaderParams<TData>): SearchDownloadHeaderColumn[] => {
  const leaves: HeaderLeaf[] = [];
  collectLeaves(columns, [], columnSizing, leaves);

  const allowedKeys = collectDownloadColumnKeys(dialogsPage);
  for (const key of collectContentSearchColumnKeys(filters)) allowedKeys.add(key);
  const shown = new Map(
    leaves
      .filter((leaf) => columnVisibility[leaf.columnId] !== false && allowedKeys.has(leaf.key))
      .map((leaf) => [leaf.columnId, leaf]),
  );

  const ordered = orderColumnIdsAsInTable([...shown.keys()], columnOrder, columnPinning).map(
    (columnId) => shown.get(columnId) as HeaderLeaf,
  );

  return buildHeaderNodes(ordered, 0);
};
